import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { createPageUrl } from "@/utils";
import { getDefaultCoverImage } from "@/utils/defaultCoverImage";

export default function ReceivedFeedback() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    loadUser();
  }, []);

  const loadUser = async () => {
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData?.session?.user) {
        navigate(createPageUrl("Auth"));
        return;
      }
      const { data: profile, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", sessionData.session.user.id)
        .single();
      
      if (error) {
        console.error("Error loading profile:", error);
        navigate(createPageUrl("Auth"));
        return;
      }
      
      setCurrentUser(profile);
    } catch (error) {
      console.error("Error loading user:", error);
      navigate(createPageUrl("Auth"));
    }
  };

  const { data: feedback, isLoading } = useQuery({
    queryKey: ["received-feedback", currentUser?.id],
    queryFn: async () => {
      if (!currentUser?.id) return [];
      
      const { data: tracks } = await supabase
        .from("tracks")
        .select("id, title, cover_image_url")
        .eq("artist_id", currentUser.id);
      
      const trackIds = tracks?.map(t => t.id) || [];
      const trackMap = new Map(tracks?.map(t => [t.id, t]) || []);
      
      if (trackIds.length === 0) return [];

      const { data: feedbackData, error: feedbackError } = await supabase
        .from("feedback")
        .select("*")
        .in("track_id", trackIds)
        .order("created_at", { ascending: false });
      
      if (feedbackError) {
        console.error("Error fetching feedback:", feedbackError);
        return [];
      }

      const mappedFeedback = (feedbackData || []).map(item => ({
        id: item.id,
        track_id: item.track_id,
        feedback_type: item.feedback_type,
        comment: item.comment,
        created_at: item.created_at,
        track: trackMap.get(item.track_id)
      }));
      
      return mappedFeedback;
    },
    enabled: !!currentUser?.id,
  });

  if (isLoading || !currentUser) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-8">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold text-foreground mb-8">Received Feedback</h1>

        {!feedback || feedback.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground mb-4">No feedback received yet.</p>
            <Button onClick={() => navigate("/Upload")} tooltip="Upload your first track to start receiving feedback">Upload a Track</Button>
          </div>
        ) : (
          <div className="grid gap-4">
            {feedback.map((item) => (
              <div key={item.id} className="bg-card p-6 rounded-lg border border-border hover:border-primary transition-colors">
                <div className="flex items-start gap-4">
                  <img
                    src={item.track?.cover_image_url || getDefaultCoverImage(item.track?.id)}
                    alt={item.track?.title}
                    className="w-20 h-20 rounded object-cover"
                  />
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-xl font-semibold text-card-foreground">
                        {item.track?.title}
                      </h3>
                      <Badge variant={
                        item.feedback_type === 'praise' ? 'default' :
                        item.feedback_type === 'scathe' ? 'destructive' :
                        'secondary'
                      }>
                        {item.feedback_type}
                      </Badge>
                    </div>
                    
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-sm text-muted-foreground">
                        Anonymous reviewer • {new Date(item.created_at).toLocaleDateString()}
                      </span>
                    </div>

                    {item.comment && (
                      <p className="text-card-foreground whitespace-pre-wrap">{item.comment}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}